import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, Settings } from 'lucide-react';
import { Button, Card, Alert } from '../design-system';
import { toast } from 'react-hot-toast';
import { apiPost } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * NotificationManager - Handles PWA push notification setup and management
 * This component manages:
 * - Notification permission requests
 * - Push subscription management
 * - Notification settings
 */
const NotificationManager = ({ userId, onSubscriptionChange }) => {
  const { user } = useAuth();
  const [permission, setPermission] = useState(Notification.permission);
  const [isSupported, setIsSupported] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const hasInitialized = useRef(false);


  // Check browser support and current permission on mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      checkSupport();
      checkCurrentSubscription();
    }
  }, []);


  const checkSupport = () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);
    
    if (!supported) {
      setError('Push notifications are not supported in this browser');
    }
  };

  const checkCurrentSubscription = async () => {
    try {
      console.log('🔔 checkCurrentSubscription called');
      const registration = await navigator.serviceWorker.ready;
      const currentSubscription = await registration.pushManager.getSubscription();
      
      console.log('🔔 Checking subscription:', {
        hasCurrentSubscription: !!currentSubscription,
        permission: Notification.permission,
        timestamp: new Date().toISOString()
      });
      
      // Set subscription state
      setSubscription(currentSubscription);
      
      // Notify parent immediately about current status
      if (onSubscriptionChange) {
        console.log('🔔 Calling onSubscriptionChange with:', !!currentSubscription);
        onSubscriptionChange(currentSubscription);
      }
      
      // Mark as initialized after checking subscription
      setIsInitialized(true);
      console.log('🔔 NotificationManager initialized');
    } catch (error) {
      console.error('Error checking subscription:', error);
      setIsInitialized(true); // Still mark as initialized even if there's an error
    }
  };

  const requestPermission = async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);

      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        toast.success('Notification permission granted!');
        await subscribeToPush();
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. You can enable it in your browser settings.');
        setError('Notification permission denied. Please enable notifications in your browser settings.');
      } else {
        toast.error('Notification permission was not granted');
      }

      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting permission:', error);
      setError('Failed to request notification permission');
      toast.error('Failed to request notification permission');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const subscribeToPush = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Get VAPID public key from environment
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      
      if (!vapidPublicKey) {
        throw new Error('VAPID public key not configured. Please set REACT_APP_VAPID_PUBLIC_KEY environment variable.');
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      setSubscription(subscription);

      // Save subscription to server
      await saveSubscriptionToServer(subscription);

      if (onSubscriptionChange) {
        onSubscriptionChange(subscription);
      }

      toast.success('Successfully subscribed to push notifications!');
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      setError('Failed to subscribe to push notifications');
      toast.error('Failed to subscribe to push notifications');
      throw error;
    }
  };

  const unsubscribeFromPush = async () => {
    try {
      setIsLoading(true);
      
      if (subscription) {
        await subscription.unsubscribe();
        setSubscription(null);
        
        // Remove subscription from server
        await removeSubscriptionFromServer();
        
        if (onSubscriptionChange) {
          onSubscriptionChange(null);
        }
        
        toast.success('Unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      setError('Failed to unsubscribe from push notifications');
      toast.error('Failed to unsubscribe from push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const saveSubscriptionToServer = async (subscription) => {
    try {
      console.log('🔔 PATIENT: Saving subscription to server...');
      console.log('🔔 PATIENT: User ID (should start with user_):', userId);
      console.log('🔔 PATIENT: Subscription endpoint:', subscription.endpoint);
      
      await apiPost('/.netlify/functions/save-subscription', {
        userId: userId,
        subscription: subscription
      }, userId);
      
      console.log('✅ PATIENT: Subscription saved successfully');
    } catch (error) {
      console.error('❌ PATIENT: Error saving subscription:', error);
      throw error;
    }
  };

  const removeSubscriptionFromServer = async () => {
    try {
      await apiPost('/.netlify/functions/remove-subscription', {
        userId: userId
      }, userId);
    } catch (error) {
      console.error('Error removing subscription:', error);
      throw error;
    }
  };

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const getStatusMessage = () => {
    if (!isSupported) {
      return 'Push notifications are not supported in this browser';
    }
    
    if (permission === 'granted' && subscription) {
      return 'You will receive notifications for new tasks';
    }
    
    if (permission === 'granted' && !subscription) {
      return 'Permission granted but not subscribed to notifications';
    }
    
    if (permission === 'denied') {
      return 'Notification permission denied. Enable in browser settings.';
    }
    
    return 'Click to enable push notifications';
  };

  const getStatusColor = () => {
    if (!isSupported || permission === 'denied') return 'red';
    if (permission === 'granted' && subscription) return 'green';
    return 'blue';
  };

  if (!isSupported) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <BellOff className="w-5 h-5 text-gray-400" />
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-500">Not supported in this browser</p>
          </div>
        </div>
      </Card>
    );
  }

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-500">Checking notification status...</p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {permission === 'granted' && subscription ? (
            <Bell className="w-5 h-5 text-green-500" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
          <div>
            <h3 className="font-medium text-gray-900">Push Notifications</h3>
            <p className={`text-sm text-${getStatusColor()}-600`}>
              {getStatusMessage()}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          {permission === 'granted' && subscription ? (
            <Button
              variant="outline"
              size="sm"
              onClick={unsubscribeFromPush}
              disabled={isLoading}
            >
              {isLoading ? 'Unsubscribing...' : 'Disable'}
            </Button>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={requestPermission}
              disabled={isLoading || permission === 'denied'}
            >
              {isLoading ? 'Setting up...' : 'Enable'}
            </Button>
          )}
        </div>
      </div>
      
      {error && (
        <Alert variant="error" className="mt-3">
          {error}
        </Alert>
      )}
    </Card>
  );
};

export default NotificationManager;
